import React, { useMemo, useState } from 'react';
import {
  slotForItem,
  validSlotsFor,
  equippedItem,
  computeAc,
  describeAc,
  weaponMeta,
  isProficientWith,
  isShield,
  equipPenalty,
} from '../../lib/equip';

// Equip page — shown as the "Equipped" tab inside InventoryPanel. Lists the
// three slots (armor / mainHand / offHand) with their current item +
// derived AC + a "Change…" inline picker.
export default function EquipTab({ playerRecord, onEquip, editable }) {
  if (!playerRecord) return <div style={styles.empty}>No character loaded.</div>;

  const inventory = playerRecord.inventory || [];
  const armorItem    = equippedItem(inventory, 'armor');
  const mainHandItem = equippedItem(inventory, 'mainHand');
  const offHandItem  = equippedItem(inventory, 'offHand');
  const totalAc      = computeAc(playerRecord);
  const acDesc       = describeAc(playerRecord);

  return (
    <div style={styles.wrap}>
      <div style={styles.summary}>
        <div style={styles.acBig}>
          <span style={styles.acLabel}>AC</span>
          <span style={styles.acValue}>{totalAc}</span>
        </div>
        <div style={styles.acBreakdown}>{acDesc.breakdown}</div>
      </div>

      <SlotCard
        label="Armor"
        item={armorItem}
        slot="armor"
        inventory={inventory}
        playerRecord={playerRecord}
        onEquip={onEquip}
        editable={editable}
        emptyHint="Unarmored — base AC 10 + DEX mod"
      />
      <SlotCard
        label="Main hand"
        item={mainHandItem}
        slot="mainHand"
        inventory={inventory}
        playerRecord={playerRecord}
        onEquip={onEquip}
        editable={editable}
        emptyHint="Empty — no quick-attack available"
      />
      <SlotCard
        label="Off hand"
        item={offHandItem}
        slot="offHand"
        inventory={inventory}
        playerRecord={playerRecord}
        onEquip={onEquip}
        editable={editable}
        emptyHint="Empty — equip a shield (+2 AC) or a light weapon"
      />
    </div>
  );
}

function SlotCard({ label, item, slot, inventory, playerRecord, onEquip, editable, emptyHint }) {
  const [picking, setPicking] = useState(false);

  // Items eligible for this slot. Honour `validSlotsFor()` so a Longsword
  // (default mainHand) still surfaces in the off-hand picker — letting the
  // player explicitly equip it there.
  const eligible = useMemo(() => inventory.filter((i) => {
    if ((i.qty || 0) <= 0) return false;
    return validSlotsFor(i).includes(slot);
  }), [inventory, slot]);

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.cardLabel}>{label}</span>
        {editable && eligible.length > 0 && (
          <button style={styles.changeBtn} onClick={() => setPicking((v) => !v)}>
            {picking ? 'Cancel' : (item ? 'Change' : 'Equip')}
          </button>
        )}
      </div>

      {item ? (
        <ItemSummary item={item} slot={slot} playerRecord={playerRecord} />
      ) : (
        <div style={styles.empty}>{emptyHint}</div>
      )}

      {item && (() => {
        const pen = equipPenalty(item, playerRecord);
        if (!pen) return null;
        return (
          <div style={{
            marginTop: 6, padding: '6px 8px',
            background: 'rgba(251,146,60,0.12)',
            border: '1px solid #fb923c',
            borderRadius: 4,
            fontSize: 11,
            color: '#fb923c',
            lineHeight: 1.4,
          }}>
            <strong>⚠ {pen.label}.</strong> {pen.detail}
          </div>
        );
      })()}

      {editable && item && (
        <button style={styles.unequipBtn} onClick={() => onEquip?.(item.id, slot)}>
          Unequip
        </button>
      )}

      {picking && eligible.length > 0 && (
        <div style={styles.pickerList}>
          {eligible.map((eq) => {
            const isInThisSlot = eq.id === item?.id;
            const naturalSlot  = slotForItem(eq);
            const isOffHandWeapon = slot === 'offHand' && eq.category === 'weapon' && naturalSlot !== 'offHand';
            // Soft warning preview — picker doesn't block; the equipped card
            // surfaces the full penalty.
            const penaltyPreview = equipPenalty({ ...eq, equipped: true }, playerRecord);
            return (
              <button
                key={eq.id}
                style={{ ...styles.pickerRow, ...(isInThisSlot ? styles.pickerRowActive : {}) }}
                onClick={() => { onEquip?.(eq.id, slot); setPicking(false); }}
                title={penaltyPreview ? penaltyPreview.detail : `Equip ${eq.name}`}
              >
                <span style={styles.pickerName}>
                  {eq.name}
                  {isOffHandWeapon && <span style={styles.pickerOff}> · off-hand</span>}
                  {penaltyPreview && <span style={{ color: '#fb923c', fontSize: 10, marginLeft: 6 }}>⚠ {penaltyPreview.label}</span>}
                </span>
                <span style={styles.pickerMeta}>{previewMeta(eq, playerRecord)}</span>
              </button>
            );
          })}
        </div>
      )}
      {picking && eligible.length === 0 && (
        <div style={styles.empty}>Nothing in inventory fits this slot.</div>
      )}
    </div>
  );
}

function ItemSummary({ item, slot, playerRecord }) {
  if (slot === 'armor') {
    const acDesc = describeAc(playerRecord);
    return (
      <div style={styles.itemBox}>
        <div style={styles.itemName}>{item.name}</div>
        <div style={styles.itemMeta}>{acDesc.breakdown}</div>
        {item.description && <div style={styles.itemDesc}>{item.description}</div>}
      </div>
    );
  }
  if (slot === 'mainHand' || slot === 'offHand') {
    if (isShield(item)) {
      return (
        <div style={styles.itemBox}>
          <div style={styles.itemName}>{item.name}</div>
          <div style={styles.itemMeta}>+2 AC</div>
          {item.description && <div style={styles.itemDesc}>{item.description}</div>}
        </div>
      );
    }
    const meta = weaponMeta(item);
    if (!meta) {
      return (
        <div style={styles.itemBox}>
          <div style={styles.itemName}>{item.name}</div>
          {item.description && <div style={styles.itemDesc}>{item.description}</div>}
        </div>
      );
    }
    const profOK = isProficientWith(item, playerRecord?.weaponProficiencies);
    return (
      <div style={styles.itemBox}>
        <div style={styles.itemName}>
          {item.name}
          {profOK && <span style={styles.profBadge}>prof</span>}
        </div>
        <div style={styles.itemMeta}>
          {meta.damageDie} {meta.damageType} · {meta.stat?.toUpperCase()}
          {meta.properties?.length > 0 && ` · ${meta.properties.join(', ')}`}
        </div>
        {item.description && <div style={styles.itemDesc}>{item.description}</div>}
      </div>
    );
  }
  return null;
}

function previewMeta(item, playerRecord) {
  if (item.category === 'armor') {
    if (isShield(item)) return '+2 AC';
    // Cheap preview — full breakdown only on the equipped card.
    return item.description?.split('·')[0]?.trim() || 'armor';
  }
  if (item.category === 'weapon') {
    const meta = weaponMeta(item);
    if (!meta) return '';
    const profOK = isProficientWith(item, playerRecord?.weaponProficiencies);
    return `${meta.damageDie}${profOK ? ' · prof' : ''}`;
  }
  return '';
}

const styles = {
  wrap:        { display: 'flex', flexDirection: 'column', gap: 8 },

  summary:     { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'rgba(212,168,71,0.1)', border: '1px solid #8b7340', borderRadius: 6 },
  acBig:       { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 },
  acLabel:     { fontSize: 10, color: '#9a8b6e', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' },
  acValue:     { fontSize: 32, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", lineHeight: 1 },
  acBreakdown: { flex: 1, fontSize: 11, color: '#9a8b6e', lineHeight: 1.4 },

  card:        { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  cardHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardLabel:   { fontSize: 10, color: '#9a8b6e', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' },
  changeBtn:   { background: 'rgba(212,168,71,0.12)', border: '1px solid rgba(212,168,71,0.4)', color: '#d4a847', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  unequipBtn:  { alignSelf: 'flex-start', background: 'transparent', border: '1px solid #4a3d28', color: '#9a8b6e', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  empty:       { fontSize: 11, color: '#6a5d45', fontStyle: 'italic', padding: '4px 0' },

  itemBox:     { display: 'flex', flexDirection: 'column', gap: 3 },
  itemName:    { fontSize: 14, fontWeight: 700, color: '#f0e6d0', display: 'flex', alignItems: 'center', gap: 6 },
  itemMeta:    { fontSize: 11, color: '#7ab8e0', fontWeight: 600 },
  itemDesc:    { fontSize: 11, color: '#9a8b6e', lineHeight: 1.4, fontStyle: 'italic' },
  profBadge:   { fontSize: 9, fontWeight: 800, color: '#86efac', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', borderRadius: 3, padding: '1px 5px', letterSpacing: '0.04em' },

  pickerList:  { display: 'flex', flexDirection: 'column', gap: 4, padding: 6, background: '#0a1426', border: '1px solid #d4a847', borderRadius: 4 },
  pickerRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  pickerRowActive: { borderColor: '#d4a847', background: 'rgba(212,168,71,0.1)' },
  pickerName:  { fontSize: 12, fontWeight: 700 },
  pickerOff:   { fontSize: 10, color: '#7ab8e0', fontWeight: 600, marginLeft: 4 },
  pickerMeta:  { fontSize: 10, color: '#9a8b6e' },
};
